import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  ReactNode,
} from 'react';
import { locationApi, seedMyLocationsCache } from '../services/api';
import { Location, Employee } from '../types';
import { isLocationActive } from '../utils/locationDisplay';
import { useAuth } from './AuthContext';

const LOCATIONS_CACHE_KEY = 'locationsCache';
const LOCATIONS_SESSION_KEY = 'staffly:locationsSession';
const MINE_FETCH_TIMEOUT_MS = 8000;

export interface LocationContextType {
  /** Immediate selection — use for header UI and localStorage. */
  selectedLocationId: number | null;
  setSelectedLocationId: (id: number | null) => void;
  /** Debounced selection — prefer for API calls and heavy page filters. */
  debouncedSelectedLocationId: number | null;
  locations: Location[];
  /** True only when there are no locations to show yet (first load, no cache). */
  locationsLoading: boolean;
  /** Background refresh while cached locations remain visible. */
  locationsRefreshing: boolean;
  selectedLocation: Location | null;
  isSelectedLocationInactive: boolean;
  employeeDetailsReportLocationLocked: boolean;
  lockEmployeeDetailsReportLocation: () => void;
  unlockEmployeeDetailsReportLocation: () => void;
  documentsPoliciesLocationLocked: boolean;
  lockDocumentsPoliciesLocation: () => void;
  unlockDocumentsPoliciesLocation: () => void;
  reloadLocations: () => Promise<void>;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

interface LocationsSnapshot {
  locations: Location[];
  selectedLocationId: number | null;
  employeeId: number;
  updatedAt: number;
}

function readStoredEmployee(): Employee | null {
  try {
    const saved = localStorage.getItem('employee');
    if (!saved) return null;
    return JSON.parse(saved) as Employee;
  } catch (_) {
    return null;
  }
}

function readStoredEmployeeId(): number | null {
  const id = readStoredEmployee()?.id;
  return typeof id === 'number' ? id : null;
}

function readLocationsFromEmployee(emp?: Employee | null): Location[] {
  const assigned = emp?.assignedLocations;
  if (!Array.isArray(assigned) || assigned.length === 0) return [];
  return assigned.filter((loc) => loc && typeof loc.id === 'number' && loc.id > 0);
}

function readMainLocationId(emp?: Employee | null): number | null {
  const mainId = emp?.profile?.mainLocationId;
  return typeof mainId === 'number' && mainId > 0 ? mainId : null;
}

function readSessionSnapshot(employeeId?: number | null): LocationsSnapshot | null {
  try {
    const raw = sessionStorage.getItem(LOCATIONS_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LocationsSnapshot;
    if (!parsed.locations?.length || !parsed.employeeId) return null;
    if (employeeId != null && parsed.employeeId !== employeeId) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function readCachedLocations(employeeId?: number | null): Location[] {
  const fromEmployee = readLocationsFromEmployee(
    employeeId != null
      ? (readStoredEmployee()?.id === employeeId ? readStoredEmployee() : null)
      : readStoredEmployee()
  );
  if (fromEmployee.length) return fromEmployee;

  const session = readSessionSnapshot(employeeId);
  if (session?.locations.length) {
    return session.locations;
  }

  try {
    const cached = localStorage.getItem(LOCATIONS_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached) as {
        locations?: Location[];
        employeeId?: number;
      };
      const cachedLocations = parsed.locations;
      const cachedEmployeeId = parsed.employeeId;
      if (
        cachedLocations?.length &&
        (employeeId == null || cachedEmployeeId == null || cachedEmployeeId === employeeId)
      ) {
        return cachedLocations;
      }
    }
  } catch (_) {
    /* ignore */
  }
  return [];
}

function persistLocationsSnapshot(
  locations: Location[],
  selectedLocationId: number | null,
  employeeId: number
): void {
  const snapshot: LocationsSnapshot = {
    locations,
    selectedLocationId,
    employeeId,
    updatedAt: Date.now(),
  };
  try {
    sessionStorage.setItem(LOCATIONS_SESSION_KEY, JSON.stringify(snapshot));
    localStorage.setItem(
      LOCATIONS_CACHE_KEY,
      JSON.stringify({
        locations,
        employeeId,
        timestamp: Date.now(),
      })
    );
  } catch (_) {
    /* ignore */
  }
}

function readDeepLinkLocationId(): number | null {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get('locationId');
  if (!raw) return null;
  const id = parseInt(raw, 10);
  return Number.isNaN(id) ? null : id;
}

function pickMainOrFirst(locations: Location[], emp?: Employee | null): number | null {
  const mainId = readMainLocationId(emp);
  if (mainId != null && (locations.length === 0 || locations.some((l) => l.id === mainId))) {
    return mainId;
  }
  return locations[0]?.id ?? mainId ?? null;
}

function readInitialSelectedLocationId(
  locations: Location[],
  employeeId?: number | null,
  emp?: Employee | null
): number | null {
  const deepLinkLocationId = readDeepLinkLocationId();
  if (deepLinkLocationId != null) {
    if (locations.length === 0 || locations.some((l) => l.id === deepLinkLocationId)) {
      return deepLinkLocationId;
    }
  }

  const session = readSessionSnapshot(employeeId);
  if (session?.selectedLocationId != null) {
    if (locations.length === 0 || locations.some((l) => l.id === session.selectedLocationId)) {
      return session.selectedLocationId;
    }
  }

  const saved = localStorage.getItem('selectedLocationId');
  const savedId = saved ? Number(saved) : null;
  if (savedId && !Number.isNaN(savedId)) {
    if (locations.length === 0 || locations.some((l) => l.id === savedId)) return savedId;
  }

  return pickMainOrFirst(locations, emp);
}

function resolveSelectedLocationId(
  locationsData: Location[],
  emp: Employee,
  currentId: number | null,
  preferMain: boolean
): number | null {
  if (preferMain) {
    return pickMainOrFirst(locationsData, emp);
  }
  if (locationsData.length === 0) return readMainLocationId(emp);

  if (currentId && locationsData.some((loc) => loc.id === currentId)) return currentId;

  const savedLocationId = localStorage.getItem('selectedLocationId');
  const savedId = savedLocationId ? Number(savedLocationId) : null;
  if (savedId && locationsData.some((loc) => loc.id === savedId)) return savedId;
  return pickMainOrFirst(locationsData, emp);
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error('Location fetch timed out')), ms);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        window.clearTimeout(timer);
        reject(err);
      }
    );
  });
}

export const LocationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { employee } = useAuth();
  const storedEmployee = readStoredEmployee();
  const storedEmployeeId = readStoredEmployeeId();
  const cacheEmployeeId = employee?.id ?? storedEmployeeId;
  const initialEmployee = employee ?? storedEmployee;
  const initialCached = readCachedLocations(cacheEmployeeId);
  const initialSelectedId = readInitialSelectedLocationId(
    initialCached,
    cacheEmployeeId,
    initialEmployee
  );

  const [locations, setLocations] = useState<Location[]>(initialCached);
  const [selectedLocationId, setSelectedLocationIdState] = useState<number | null>(initialSelectedId);
  const [locationsLoading, setLocationsLoading] = useState(
    () => initialCached.length === 0 && initialSelectedId == null
  );
  const [locationsRefreshing, setLocationsRefreshing] = useState(false);
  const [employeeDetailsReportLocationLocked, setEmployeeDetailsReportLocationLocked] =
    useState(false);
  const [documentsPoliciesLocationLocked, setDocumentsPoliciesLocationLocked] = useState(false);
  const selectedLocationIdRef = useRef<number | null>(initialSelectedId);
  const savedLocationBeforeEmployeeDetailsReportRef = useRef<number | null>(null);
  const savedLocationBeforeDocumentsPoliciesRef = useRef<number | null>(null);
  const employeeDetailsReportLocationLockedRef = useRef(false);
  const documentsPoliciesLocationLockedRef = useRef(false);
  const reloadInFlightRef = useRef(false);
  const lastHydratedEmployeeIdRef = useRef<number | null>(cacheEmployeeId);

  const debouncedSelectedLocationId = selectedLocationId;

  const setSelectedLocationId = useCallback((id: number | null) => {
    setSelectedLocationIdState(id);
    selectedLocationIdRef.current = id;
  }, []);

  useEffect(() => {
    selectedLocationIdRef.current = selectedLocationId;
  }, [selectedLocationId]);

  const lockEmployeeDetailsReportLocation = useCallback(() => {
    savedLocationBeforeEmployeeDetailsReportRef.current = selectedLocationIdRef.current;
    employeeDetailsReportLocationLockedRef.current = true;
    setEmployeeDetailsReportLocationLocked(true);
    setSelectedLocationIdState(null);
    selectedLocationIdRef.current = null;
  }, []);

  const unlockEmployeeDetailsReportLocation = useCallback(() => {
    employeeDetailsReportLocationLockedRef.current = false;
    setEmployeeDetailsReportLocationLocked(false);
    const id = savedLocationBeforeEmployeeDetailsReportRef.current;
    savedLocationBeforeEmployeeDetailsReportRef.current = null;
    setSelectedLocationIdState(id);
    selectedLocationIdRef.current = id;
  }, []);

  const lockDocumentsPoliciesLocation = useCallback(() => {
    savedLocationBeforeDocumentsPoliciesRef.current = selectedLocationIdRef.current;
    documentsPoliciesLocationLockedRef.current = true;
    setDocumentsPoliciesLocationLocked(true);
    setSelectedLocationIdState(null);
    selectedLocationIdRef.current = null;
  }, []);

  const unlockDocumentsPoliciesLocation = useCallback(() => {
    documentsPoliciesLocationLockedRef.current = false;
    setDocumentsPoliciesLocationLocked(false);
    const id = savedLocationBeforeDocumentsPoliciesRef.current;
    savedLocationBeforeDocumentsPoliciesRef.current = null;
    setSelectedLocationIdState(id);
    selectedLocationIdRef.current = id;
  }, []);

  const applyLocations = useCallback(
    (locationsData: Location[], emp: Employee, preferMain: boolean) => {
      setLocations(locationsData);
      if (locationsData.length > 0) {
        seedMyLocationsCache(locationsData);
      }

      const locationLocked =
        documentsPoliciesLocationLockedRef.current || employeeDetailsReportLocationLockedRef.current;
      if (!locationLocked) {
        const nextId = resolveSelectedLocationId(
          locationsData,
          emp,
          selectedLocationIdRef.current,
          preferMain
        );
        setSelectedLocationIdState(nextId);
        selectedLocationIdRef.current = nextId;
        persistLocationsSnapshot(locationsData, nextId, emp.id);
      } else {
        persistLocationsSnapshot(locationsData, selectedLocationIdRef.current, emp.id);
      }
    },
    []
  );

  const reloadLocations = useCallback(async (preferMain = false) => {
    if (!employee?.id) {
      setLocations([]);
      setSelectedLocationIdState(null);
      selectedLocationIdRef.current = null;
      setLocationsLoading(false);
      setLocationsRefreshing(false);
      return;
    }

    const fromEmployee = readLocationsFromEmployee(employee);
    const mainId = readMainLocationId(employee);
    if (fromEmployee.length > 0) {
      applyLocations(fromEmployee, employee, preferMain);
      setLocationsLoading(false);
    } else if (mainId != null && selectedLocationIdRef.current == null) {
      setSelectedLocationIdState(mainId);
      selectedLocationIdRef.current = mainId;
      setLocationsLoading(false);
    }

    if (reloadInFlightRef.current) return;
    reloadInFlightRef.current = true;

    const hasVisibleLocations = fromEmployee.length > 0 || locations.length > 0;
    const hasKnownSelection = selectedLocationIdRef.current != null || mainId != null;
    if (!hasVisibleLocations && !hasKnownSelection) {
      setLocationsLoading(true);
    } else {
      setLocationsRefreshing(true);
    }

    try {
      const locationsData = await withTimeout(locationApi.getMine(), MINE_FETCH_TIMEOUT_MS);
      applyLocations(locationsData, employee, preferMain && locationsData.length > 0);
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLocationsLoading(false);
      setLocationsRefreshing(false);
      reloadInFlightRef.current = false;
    }
  }, [applyLocations, employee, locations.length]);

  useEffect(() => {
    reloadInFlightRef.current = false;
    if (!employee?.id) {
      lastHydratedEmployeeIdRef.current = null;
      setLocations([]);
      setSelectedLocationIdState(null);
      selectedLocationIdRef.current = null;
      setLocationsLoading(false);
      setLocationsRefreshing(false);
      return;
    }

    const isFreshLogin = lastHydratedEmployeeIdRef.current !== employee.id;
    if (isFreshLogin) {
      const fromEmployee = readLocationsFromEmployee(employee);
      if (fromEmployee.length > 0) {
        applyLocations(fromEmployee, employee, true);
        setLocationsLoading(false);
      } else {
        // Drop the previous user's list so the header never shows stale workplaces.
        setLocations([]);
        const mainId = readMainLocationId(employee);
        if (mainId != null) {
          setSelectedLocationIdState(mainId);
          selectedLocationIdRef.current = mainId;
          setLocationsLoading(false);
        }
      }
    }
    lastHydratedEmployeeIdRef.current = employee.id;
    void reloadLocations(isFreshLogin);
    // Fresh login must select the user's main location; later refreshes keep the current choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee?.id]);

  useEffect(() => {
    if (!employee?.id) return;
    const fromEmployee = readLocationsFromEmployee(employee);
    if (fromEmployee.length === 0 || locations.length > 0) return;
    applyLocations(fromEmployee, employee, true);
    setLocationsLoading(false);
  }, [applyLocations, employee, locations.length]);

  useEffect(() => {
    if (selectedLocationId) {
      localStorage.setItem('selectedLocationId', selectedLocationId.toString());
    }
    if (employee?.id && locations.length > 0) {
      persistLocationsSnapshot(locations, selectedLocationId, employee.id);
    }
  }, [selectedLocationId, locations, employee?.id]);

  const selectedLocation = useMemo(
    () =>
      selectedLocationId != null
        ? locations.find((l) => l.id === selectedLocationId) ?? null
        : null,
    [locations, selectedLocationId]
  );

  const isSelectedLocationInactive = useMemo(
    () => selectedLocation != null && !isLocationActive(selectedLocation),
    [selectedLocation]
  );

  const value = useMemo<LocationContextType>(
    () => ({
      selectedLocationId,
      setSelectedLocationId,
      debouncedSelectedLocationId,
      locations,
      locationsLoading,
      locationsRefreshing,
      selectedLocation,
      isSelectedLocationInactive,
      employeeDetailsReportLocationLocked,
      lockEmployeeDetailsReportLocation,
      unlockEmployeeDetailsReportLocation,
      documentsPoliciesLocationLocked,
      lockDocumentsPoliciesLocation,
      unlockDocumentsPoliciesLocation,
      reloadLocations: () => reloadLocations(false),
    }),
    [
      selectedLocationId,
      setSelectedLocationId,
      debouncedSelectedLocationId,
      locations,
      locationsLoading,
      locationsRefreshing,
      selectedLocation,
      isSelectedLocationInactive,
      employeeDetailsReportLocationLocked,
      lockEmployeeDetailsReportLocation,
      unlockEmployeeDetailsReportLocation,
      documentsPoliciesLocationLocked,
      lockDocumentsPoliciesLocation,
      unlockDocumentsPoliciesLocation,
      reloadLocations,
    ]
  );

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
};

export function useLocationContext(): LocationContextType {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocationContext must be used within a LocationProvider');
  }
  return context;
}
